import { useEffect, useState } from 'react';

import { RichTextDisplay } from '@bonusly/common-rich-text-editor';
import { Box, Text } from '@bonusly/design-system';

interface BizyAnimatedMessageProps {
	content: string;
	isComplete?: boolean;
	onContentUpdate?: () => void;
}

export function BizyAnimatedMessage({ content, isComplete = true, onContentUpdate }: BizyAnimatedMessageProps) {
	const [displayedContent, setDisplayedContent] = useState('');
	const [displayedIndex, setDisplayedIndex] = useState(0);

	useEffect(() => {
		if (!content || displayedIndex >= content.length) return;

		// reveal content character by character
		const timeout = setTimeout(() => {
			setDisplayedIndex((prev) => prev + 1);
		}, 10); // 10ms per character

		return () => clearTimeout(timeout);
	}, [content, displayedIndex]);

	useEffect(() => {
		setDisplayedContent(content.substring(0, displayedIndex));
		onContentUpdate?.();
	}, [displayedIndex, content, onContentUpdate]);

	return (
		<Box
			alignSelf="flex-start"
			maxWidth="85%"
			bg="bg.subtle"
			color="text.default"
			px={3}
			py={2}
			borderRadius="lg"
			borderBottomLeftRadius="sm"
			sx={{
				'p + p': { marginTop: '0.5em' },
				'ul, ol': { marginTop: '0.5em', marginBottom: '0.5em' },
				'p:first-of-type': { marginTop: 0 },
				'p:last-of-type': { marginBottom: 0 },
			}}
		>
			<RichTextDisplay fontSize="sm">{displayedContent}</RichTextDisplay>
			{!isComplete && (
				<Text fontSize="xs" color="text.softest" mt={1}>
					●
				</Text>
			)}
		</Box>
	);
}
